import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import DiamondRounded from '@mui/icons-material/DiamondRounded';
import HotelRounded from '@mui/icons-material/HotelRounded';
import StorefrontRounded from '@mui/icons-material/StorefrontRounded';
import WatchRounded from '@mui/icons-material/WatchRounded';
import LocalPharmacyRounded from '@mui/icons-material/LocalPharmacyRounded';
import Inventory2Rounded from '@mui/icons-material/Inventory2Rounded';
import VisibilityRounded from '@mui/icons-material/VisibilityRounded';
import SpaRounded from '@mui/icons-material/SpaRounded';
import SchoolRounded from '@mui/icons-material/SchoolRounded';
import ConstructionRounded from '@mui/icons-material/ConstructionRounded';
import ArchitectureRounded from '@mui/icons-material/ArchitectureRounded';
import GavelRounded from '@mui/icons-material/GavelRounded';
import WeekendRounded from '@mui/icons-material/WeekendRounded';
import ApartmentRounded from '@mui/icons-material/ApartmentRounded';
import SelfImprovementRounded from '@mui/icons-material/SelfImprovementRounded';
import NorthEastRounded from '@mui/icons-material/NorthEastRounded';
import ReceiptLongRounded from '@mui/icons-material/ReceiptLongRounded';
import CreditCardOffRounded from '@mui/icons-material/CreditCardOffRounded';
import TranslateRounded from '@mui/icons-material/TranslateRounded';
import BoltRounded from '@mui/icons-material/BoltRounded';
import { useTranslation } from 'react-i18next';

import AppAppBar from '../components/AppAppBar';
import Footer from '../components/Footer';
import { Section, Eyebrow } from '../components/Section';
import Reveal from '../components/Reveal';
import NizroShowcase from '../components/NizroShowcase';
import NizroTimeline from '../components/NizroTimeline';
import { Seo } from '../components/Seo';
import { nizroHomeUrl, nizroPageUrl, nizroSignupUrl } from '../links';
import { azure, emerald, glass, glassHover, line, slate } from '../theme';
import wordmark from '../assets/img/nizro/nizro-wordmark-reversed.svg';
import appIcon from '../assets/img/nizro/nizro-icon-rounded.svg';

/**
 * /nizro: a showcase that hands visitors on to nizro.app.
 *
 * nizro.app is where the product lives, with a page per trade, the pricing
 * and the signup, so this page does not try to repeat it. It shows the
 * product (logo, real screens), says the four things that matter, and then
 * every route out goes to nizro.app: the trial buttons to /signup, and each
 * trade tile to that trade's own page there. Only the Fawtara timeline stays
 * in full, because it is this site's own e-invoicing story.
 *
 * The trade list mirrors nizro.app's, including its split between the trades
 * that have screens of their own and those that arrive fully configured. If
 * nizro.app adds a trade, add it here with its /solutions/<slug>.
 */
const TRADES_OWN = [
  { key: 'gold', slug: 'gold', Icon: DiamondRounded },
  { key: 'hotel', slug: 'hotel', Icon: HotelRounded },
  { key: 'dental', slug: 'dental', Icon: LocalPharmacyRounded },
  { key: 'perfume', slug: 'perfume', Icon: SpaRounded },
  { key: 'furniture', slug: 'furniture', Icon: WeekendRounded },
  { key: 'law', slug: 'law', Icon: GavelRounded },
];
const TRADES_READY = [
  { key: 'realestate', slug: 'real-estate', Icon: ApartmentRounded },
  { key: 'optician', slug: 'optician', Icon: VisibilityRounded },
  { key: 'retail', slug: 'general-trade', Icon: StorefrontRounded },
  { key: 'silver', slug: 'silver-watches', Icon: WatchRounded },
  { key: 'food', slug: 'food-wholesale', Icon: Inventory2Rounded },
  { key: 'spa', slug: 'salon-spa', Icon: SelfImprovementRounded },
  { key: 'training', slug: 'gym', Icon: SchoolRounded },
  { key: 'fitout', slug: 'fit-out', Icon: ConstructionRounded },
  { key: 'engineering', slug: 'engineering', Icon: ArchitectureRounded },
];
const TRADE_COUNT = TRADES_OWN.length + TRADES_READY.length;

const PROMISES = [
  { key: 'nocard', Icon: CreditCardOffRounded },
  { key: 'ready', Icon: BoltRounded },
  { key: 'fawtara', Icon: ReceiptLongRounded },
  { key: 'bilingual', Icon: TranslateRounded },
];

// ↗ means "opens nizro.app". It points up and away from the reading
// direction, so it is mirrored on Arabic pages.
const outIconSx = { '[dir="rtl"] &': { transform: 'scaleX(-1)' } };

/** The trial button and the nizro.app button, used at the top and the foot. */
function NizroActions({ lang, t, centered }) {
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={1.5}
      justifyContent={centered ? 'center' : 'flex-start'}
      alignItems={{ xs: 'stretch', sm: 'center' }}
    >
      <Button
        size="large"
        variant="contained"
        href={nizroSignupUrl(lang)}
        target="_blank"
        rel="noopener"
        endIcon={<NorthEastRounded sx={outIconSx} />}
        sx={{ px: 4, py: 1.4, fontWeight: 800 }}
      >
        {t('nizro.ctaPrimary')}
      </Button>
      <Button
        size="large"
        variant="outlined"
        href={nizroHomeUrl(lang)}
        target="_blank"
        rel="noopener"
        sx={{ px: 3.5, py: 1.4, fontWeight: 700 }}
      >
        {t('nizro.ctaSite')}
      </Button>
    </Stack>
  );
}

function TradeTile({ trade, featured, lang, t }) {
  const { key, slug, Icon } = trade;
  const name = t(`nizro.trade.${key}`);
  return (
    <Box
      component="a"
      href={nizroPageUrl(lang, `solutions/${slug}`)}
      target="_blank"
      rel="noopener"
      aria-label={t('nizro.tradeOpens', { trade: name })}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2,
        py: 1.5,
        borderRadius: '16px',
        border: `1px solid ${featured ? alpha(azure[400], 0.32) : line}`,
        background: featured ? `linear-gradient(135deg, ${alpha(azure[500], 0.1)}, transparent 70%)` : 'transparent',
        color: 'text.primary',
        textDecoration: 'none',
        transition: 'border-color .2s ease, background-color .2s ease',
        '& .dc-out': { opacity: 0.5, transition: 'opacity .2s ease, transform .2s ease' },
        '&:hover': {
          borderColor: alpha(azure[300], 0.6),
          bgcolor: glassHover,
          '& .dc-out': { opacity: 1, transform: 'translate(2px, -2px)' },
        },
        '[dir="rtl"] &:hover .dc-out': { transform: 'translate(-2px, -2px) scaleX(-1)' },
        '&:focus-visible': { outline: `2px solid ${azure[300]}`, outlineOffset: 2 },
      }}
    >
      <Box
        sx={{
          display: 'grid',
          placeItems: 'center',
          width: 38,
          height: 38,
          borderRadius: '11px',
          bgcolor: alpha(featured ? azure[500] : slate[400], featured ? 0.16 : 0.1),
          color: featured ? azure[200] : azure[300],
          flex: 'none',
        }}
      >
        <Icon sx={{ fontSize: 20 }} />
      </Box>
      <Typography sx={{ flex: 1, fontWeight: 700, fontSize: 15, lineHeight: 1.3 }}>{name}</Typography>
      <NorthEastRounded className="dc-out" sx={{ fontSize: 18, color: azure[300], ...outIconSx }} />
    </Box>
  );
}

function TradeGroup({ label, trades, featured, lang, t }) {
  return (
    <Box>
      <Typography variant="overline" component="h3" sx={{ display: 'block', color: 'text.secondary', mb: 1.5 }}>
        {label}
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gap: 1.25,
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)' },
        }}
      >
        {trades.map((trade) => (
          <TradeTile key={trade.key} trade={trade} featured={featured} lang={lang} t={t} />
        ))}
      </Box>
    </Box>
  );
}

export default function NizroPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === 'ar' ? 'ar' : 'en';

  return (
    <Box sx={{ bgcolor: 'background.default', color: 'text.primary' }}>
      <Seo page="nizro" path="/nizro" />
      <AppAppBar />
      <Box component="main">
        <Section id="nizro" sx={{ pt: { xs: 15, md: 19 } }}>
          {/* ── Hero ─────────────────────────────────────────────────────── */}
          <Reveal>
            <Box sx={{ textAlign: 'center', maxWidth: 820, mx: 'auto' }}>
              <Box
                component="img"
                src={wordmark}
                alt="Nizro"
                sx={{ display: 'block', height: { xs: 28, md: 34 }, mx: 'auto', mb: 3.5 }}
              />
              <Eyebrow sx={{ justifyContent: 'center' }}>{t('nizro.eyebrow')}</Eyebrow>
              <Typography variant="h1">{t('nizro.title')}</Typography>
              <Typography
                variant="subtitle1"
                sx={{ mt: 2.5, color: 'text.secondary', maxWidth: 680, mx: 'auto' }}
              >
                {t('nizro.lead')}
              </Typography>
              <Box sx={{ mt: 4 }}>
                <NizroActions lang={lang} t={t} centered />
              </Box>
              <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                {t('nizro.ctaNote')}
              </Typography>
            </Box>
          </Reveal>

          {/* ── The product, shown rather than described ─────────────────── */}
          <Reveal delay={120}>
            <Box sx={{ mt: { xs: 7, md: 9 } }}>
              <NizroShowcase />
            </Box>
          </Reveal>

          {/* ── The four things that matter ──────────────────────────────── */}
          <Box
            sx={{
              mt: { xs: 9, md: 12 },
              display: 'grid',
              gap: 2,
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            }}
          >
            {PROMISES.map(({ key, Icon }, i) => (
              <Reveal key={key} delay={i * 70}>
                <Stack
                  direction="row"
                  spacing={2}
                  sx={{ height: '100%', p: { xs: 2.5, md: 3 }, borderRadius: 3, border: `1px solid ${line}`, bgcolor: glass }}
                >
                  <Box
                    sx={{
                      display: 'grid',
                      placeItems: 'center',
                      width: 44,
                      height: 44,
                      borderRadius: '13px',
                      bgcolor: alpha(emerald[500], 0.12),
                      color: emerald[300],
                      flex: 'none',
                    }}
                  >
                    <Icon />
                  </Box>
                  <Box>
                    <Typography variant="h6" component="h2" sx={{ lineHeight: 1.35 }}>
                      {t(`nizro.promise.${key}.t`)}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.75, color: 'text.secondary' }}>
                      {t(`nizro.promise.${key}.d`)}
                    </Typography>
                  </Box>
                </Stack>
              </Reveal>
            ))}
          </Box>

          {/* ── Every trade, each one a door into nizro.app ──────────────── */}
          <Reveal>
            <Box sx={{ mt: { xs: 9, md: 12 } }}>
              <Typography variant="h3" component="h2">
                {t('nizro.tradesTitle', { count: TRADE_COUNT })}
              </Typography>
              <Typography sx={{ mt: 1.25, color: 'text.secondary', maxWidth: 680 }}>
                {t('nizro.tradesLead')}
              </Typography>
              <Stack spacing={4} sx={{ mt: 4 }}>
                <TradeGroup label={t('nizro.tradesOwn')} trades={TRADES_OWN} featured lang={lang} t={t} />
                <TradeGroup label={t('nizro.tradesReady')} trades={TRADES_READY} lang={lang} t={t} />
              </Stack>
            </Box>
          </Reveal>

          {/* ── Fawtara: this site's own story, kept in full ─────────────── */}
          <Reveal>
            <Box sx={{ mt: { xs: 9, md: 12 } }}>
              <NizroTimeline />
              <Button
                href={nizroPageUrl(lang, 'fawtara')}
                target="_blank"
                rel="noopener"
                endIcon={<NorthEastRounded sx={outIconSx} />}
                sx={{ mt: 2, px: 0.5, color: azure[300], '&:hover': { bgcolor: 'transparent', color: azure[200] } }}
              >
                {t('nizro.fawtaraLink')}
              </Button>
            </Box>
          </Reveal>

          {/* ── The hand-off ─────────────────────────────────────────────── */}
          <Reveal>
            <Box
              sx={{
                mt: { xs: 9, md: 12 },
                position: 'relative',
                overflow: 'hidden',
                textAlign: 'center',
                px: { xs: 3, sm: 6 },
                py: { xs: 6, md: 8 },
                borderRadius: 6,
                border: `1px solid ${alpha(azure[300], 0.28)}`,
                background: `
                  radial-gradient(640px 320px at 50% -20%, ${alpha(azure[500], 0.24)}, transparent 70%),
                  radial-gradient(520px 260px at 50% 120%, ${alpha(emerald[400], 0.12)}, transparent 70%),
                  ${glass}
                `,
              }}
            >
              <Box
                component="img"
                src={appIcon}
                alt=""
                sx={{
                  display: 'block',
                  width: 64,
                  height: 64,
                  mx: 'auto',
                  mb: 3,
                  borderRadius: '15px',
                  boxShadow: `0 0 0 1px ${alpha('#FFFFFF', 0.08)}, 0 18px 40px ${alpha(azure[600], 0.35)}`,
                }}
              />
              <Typography variant="h2" component="h2" sx={{ maxWidth: 640, mx: 'auto' }}>
                {t('nizro.final.title')}
              </Typography>
              <Typography variant="subtitle1" sx={{ mt: 2, mb: 4, color: 'text.secondary', maxWidth: 560, mx: 'auto' }}>
                {t('nizro.final.body')}
              </Typography>
              <NizroActions lang={lang} t={t} centered />
              <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                {t('nizro.ctaNote')}
              </Typography>
              <Typography
                variant="caption"
                component="p"
                sx={{ mt: 4, pt: 3, borderTop: `1px solid ${line}`, color: 'text.secondary', maxWidth: 560, mx: 'auto' }}
              >
                {t('nizro.final.trust')}
              </Typography>
            </Box>
          </Reveal>
        </Section>
      </Box>
      <Footer />
    </Box>
  );
}
